import {
    useFocusEffect,
    useRoute,
    RouteProp,
    useNavigation,
} from "@react-navigation/native";
import {
    Alert,
    Button,
    CheckBox,
    ConfirmModal,
    DataTable,
    Icon,
    Loading,
    SearchInput,
    Spacer,
    StyleFunction,
    Surface,
    useAuth,
    useModal,
    usePagination,
    useThemedStyle,
} from "@venuepos/react-common";
import {
    GQTagGroupsQuery,
    useTagGroupDeleteMutation,
    useTagGroupsQuery,
} from "graphql-sdk";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";

import { useHandleMutationError } from "../../../hooks/use-handle-mutation-error";
import { useMultiSelect } from "../../../hooks/use-multi-select";
import { RootStackParamList } from "../../../navigation";
import { Row } from "./row";

export function TagGroupsTab() {
    const auth = useAuth();
    auth.enforce("merchant.tag");

    const route = useRoute<RouteProp<RootStackParamList, "TAG_GROUPS">>();
    const { navigate } = useNavigation();

    const styles = useThemedStyle(styleFunc);
    const [t] = useTranslation();
    const { render } = useModal();
    const { handleMutationError } = useHandleMutationError();
    const [deleteTagGroup] = useTagGroupDeleteMutation();

    const {
        page,
        pageSize,
        sortBy,
        sortDirection,
        onSortChange,
        onPageChange,
        onPageSizeChange,
    } = usePagination({ initialSortBy: "name" });
    const [search, setSearch] = useState<string>("");

    const {
        selectedItems: selectedTagGroups,
        setSelectedItems: setSelectedTagGroups,
        handleMultiSelect,
        allItemsSelected: allRowsSelected,
        setAllItemsSelected: setAllRowsSelected,
    } = useMultiSelect<GQTagGroupsQuery["tagGroups"]["data"][0]["id"]>();

    // GraphQL
    const { data, loading, error, refetch } = useTagGroupsQuery({
        variables: {
            pagination: {
                page,
                pageSize,
                sort: sortBy,
                sortDirection: sortDirection,
            },
            search: {
                query: search,
            },
        },
    });

    useEffect(() => {
        if (route.params?.refetch) {
            refetch();
        }
    }, [refetch, route.params?.refetch]);

    useFocusEffect(
        useCallback(() => {
            refetch();
        }, [refetch])
    );

    // Actions
    const handleEdit = useCallback(
        (id: string) => {
            navigate("TAG_GROUP_EDIT", {
                id,
            });
        },
        [navigate]
    );

    const handleDelete = useCallback(
        async (id: string) => {
            if (
                !(await render(onClose => (
                    <ConfirmModal
                        headerText={t(
                            "backoffice.tags.delete_tag_group",
                            "Delete Tag Group?"
                        )}
                        bodyText={t(
                            "backoffice.layout.delete_tag_group_explain",
                            "Are you sure you want to delete this group?"
                        )}
                        onClose={onClose}
                    />
                )))
            ) {
                return;
            }

            await handleMutationError(
                async () => {
                    await deleteTagGroup({ variables: { id } });
                },
                t("common.deleted", "Deleted"),
                () => {
                    navigate("TAGS", { refetch: true });
                }
            );
        },
        [deleteTagGroup, handleMutationError, navigate, render, t]
    );

    const deleteSelectedTagGroups = useCallback(async () => {
        selectedTagGroups.forEach(async tagGroupId => {
            await handleDelete(tagGroupId).then(() => {
                setSelectedTagGroups(
                    selectedTagGroups.filter(p => p !== tagGroupId)
                );
                return;
            });
        });
    }, [handleDelete, selectedTagGroups, setSelectedTagGroups]);

    const renderTagGroupRow = useCallback(
        (item, index) => (
            <Row
                item={item}
                key={index.toString()}
                selectedTagGroup={selectedTagGroups.includes(item.id)}
                onSelect={() => handleEdit(item.id)}
                onMultiSelectChange={handleMultiSelect}
                onDelete={() => handleDelete(item.id)}
            />
        ),
        [handleDelete, handleEdit, handleMultiSelect, selectedTagGroups]
    );

    const handleSearchTextChange = useCallback(
        text => {
            setSearch(text);
            // return to first page in list
            onPageChange(0);
        },
        [onPageChange]
    );

    return (
        <>
            <View style={styles.container}>
                <View style={styles.actionButtonContainer}>
                    <Button
                        style={styles.button}
                        variant="invert"
                        disabled={!selectedTagGroups.length}
                        onPress={deleteSelectedTagGroups}
                    >
                        {t("common.delete", "Delete")}
                    </Button>
                    <Button
                        style={styles.button}
                        type="secondary"
                        onPress={() => {
                            navigate("TAG_GROUP_CREATE");
                        }}
                    >
                        {t("common.create", "Create")}
                    </Button>
                </View>
            </View>
            <SearchInput onChange={handleSearchTextChange} />
            <Surface>
                <DataTable>
                    <DataTable.Header>
                        <DataTable.Title style={styles.iconColumn}>
                            <CheckBox
                                value={allRowsSelected}
                                onValueChange={value => {
                                    const allItemIds = data!.tagGroups.data.map(
                                        p => p!.id
                                    );
                                    handleMultiSelect(value, allItemIds);
                                    setAllRowsSelected(value);
                                }}
                            />
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={sortBy === "name" && sortDirection}
                            onPress={() => onSortChange("name")}
                        >
                            {t("common.name", "Name")}
                        </DataTable.Title>
                        <DataTable.Title numeric style={styles.iconColumn}>
                            <Icon name="sort" color={styles.iconColumn.color} />
                        </DataTable.Title>
                    </DataTable.Header>
                    {error ? (
                        <Alert type="error">
                            {t(
                                "backoffice.error.from_server",
                                "There was an error: {{errorText}}",
                                {
                                    errorText: error.message,
                                }
                            )}
                        </Alert>
                    ) : loading ? (
                        <>
                            <Spacer />
                            <Loading />
                            <Spacer />
                        </>
                    ) : (
                        data && data?.tagGroups.data.map(renderTagGroupRow)
                    )}
                    <DataTable.Pagination
                        onPageChange={onPageChange}
                        pageSize={pageSize}
                        onSizeChange={onPageSizeChange}
                        page={page}
                        numberOfPages={data?.tagGroups.pagination.pages}
                        itemCount={data?.tagGroups.pagination.resultCount}
                    />
                </DataTable>
            </Surface>
        </>
    );
}

const styleFunc: StyleFunction = theme => ({
    container: {
        justifyContent: "space-between",
        marginBottom: theme.spacingScale * 2,
        flexWrap: "wrap",
    },
    actionButtonContainer: {
        flexDirection: "row",
        justifyContent: "flex-end",
    },
    button: {
        alignSelf: "flex-end",
        marginLeft: theme.spacingScale * 2,
    },
    iconColumn: {
        flexBasis: 40,
        flexGrow: 0,
        flexShrink: 0,
        color: theme.colors.secondary,
    },
});
